import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { LazyLoadImage } from "react-lazy-load-image-component";
import emailjs from "@emailjs/browser";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import imageCompression from 'browser-image-compression';

// Check if the image exists
const imageExists = (path) => {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(true);
    img.onerror = () => resolve(false);
    img.src = path;
  });
};

// Dynamically load images
const getImagePaths = async (folderName) => {
  const imagePaths = [];
  let index = 1;

  while (true) {
    const imagePath = `/images/Baza 2D & 3D/${folderName}/image${index}.jpg`;
    const exists = await imageExists(imagePath);
    if (exists) {
      imagePaths.push(imagePath);
      index++;
    } else {
      break;
    }
  }

  return imagePaths;
};

// Compress the image to 50KB
const compressImage = async (imageSource) => {
  try {
    let file;

    // Check if the image is already a Blob or File object
    if (imageSource instanceof File || imageSource instanceof Blob) {
      file = imageSource;
    } else {
      // If image is a URL, fetch the image and convert it to Blob
      const res = await fetch(imageSource);
      const blob = await res.blob();
      file = new File([blob], "image.jpg", { type: blob.type });
    }

    // Compress the image aggressively to reduce the size to 50KB
    const compressedImage = await imageCompression(file, {
      maxSizeMB: 0.05,  // Aim for max 50KB (0.05MB)
      maxWidthOrHeight: 400, // Resize to smaller dimensions if necessary
      useWebWorker: true,  // Use Web Worker for performance
      fileType: file.type, // Keep the original file type
    });

    return compressedImage; // Return the compressed image object
  } catch (error) {
    console.error("Error compressing the image:", error);
    throw error;
  }
};

// Convert image to Base64 string
const convertToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

const NavigateFloor = () => {
  const location = useLocation();
  const { customUrl1, vrPath, customUrl3, images: providedImages } = location.state || { customUrl1: "", vrPath: "", customUrl3: "", images: null };

  const [currentIndex, setCurrentIndex] = useState(0);
  const [filteredImages, setFilteredImages] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [formData, setFormData] = useState({ name: "", phone: "", email: "", floor: customUrl3, image: "" });

  useEffect(() => {
    // Nëse jepen imazhet direkt në state, përdorim ato
    if (providedImages && Array.isArray(providedImages) && providedImages.length > 0) {
      setIsLoading(false);
      setFilteredImages(providedImages);
      setFormData((prevData) => ({
        ...prevData,
        image: providedImages[0],
      }));
    } else if (customUrl1) {
      // Përndryshe, ngarko imazhet si më parë
      setIsLoading(true);
      getImagePaths(customUrl1).then((images) => {
        setFilteredImages(images);
        setIsLoading(false);
        if (images.length > 0) {
          setFormData((prevData) => ({
            ...prevData,
            image: images[0],
          }));
        }
      });
    } else {
      setIsLoading(false);
    }
  }, [customUrl1, providedImages]);

  // Përditëso imazhin në formData kur ndryshon currentIndex
  useEffect(() => {
    if (filteredImages.length > 0 && filteredImages[currentIndex]) {
      setFormData((prevData) => ({
        ...prevData,
        image: filteredImages[currentIndex],
      }));
    }
  }, [currentIndex, filteredImages]);

  const nextImage = () => setCurrentIndex((prev) => (prev + 1) % filteredImages.length);
  const prevImage = () => setCurrentIndex((prev) => (prev - 1 + filteredImages.length) % filteredImages.length);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const sendEmail = async (e) => {
    e.preventDefault();

    if (!formData.image) {
      toast.error("Image is missing!");
      return;
    }

    try {
      const compressedImage = await compressImage(formData.image);

      const base64Image = await convertToBase64(compressedImage);

      const emailData = {
        ...formData,
        image: base64Image, 
      };

      await emailjs.send(
        "service_o4uu03l", 
        "template_9ni0653", 
        emailData,
        "lZQFukxjNs4QpBw2J" 
      );

      toast.success("Thank you! Your inquiry has been sent.");
      setFormData({ name: "", phone: "", email: "", floor: customUrl3, image: "" });
      setShowForm(false);
    } catch (error) {
      console.error("Error sending email:", error);
      toast.error("Oops! Something went wrong. Please try again.");
    }
  };

  return (
    <div className="w-full pt-20 lg:pt-40 flex flex-col items-center z-60">
      <button
        className="text-white text-base lg:text-xl mt-5 tex-regular main-red-bg px-5 lg:px-10 py-[5px] lg:py-[10px] font-bold"
      >
        {vrPath ? (
          <a href={vrPath} target="_blank" rel="noopener noreferrer">VR TOUR 360°</a>
        ) : (
          <span>VR 360</span>
        )}
      </button>

      {isLoading ? (
        <p className="text-white mt-5">Duke u ngarkuar...</p>
      ) : filteredImages.length > 0 ? (
        <div className="relative w-full lg:w-[80%] mt-5 opacity-100">
          <button onClick={prevImage} className="absolute left-0 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white px-3 py-1">
            ❮
          </button>
          <LazyLoadImage className="w-full" src={filteredImages[currentIndex]} alt="Floor Preview" />
          <button onClick={nextImage} className="absolute right-0 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white px-3 py-1">
            ❯
          </button>
        </div>
      ) : (
        <p className="text-white mt-5">Nuk ka imazhe.</p>
      )}

      <button
        onClick={() => {
          setShowForm(true);
        }}
        className="px-[25px] mt-5 w-fit py-[5px] lg:py-[10px] mb-20 text-base lg:text-xl main-red-bg tex-regular font-light text-white"
      >
        PYET PER CMIMIN
      </button>

      {showForm && (
        <div className="fixed inset-0 bg-black pt-[10vh] lg:pt-0 bg-opacity-75 flex z-50 justify-center items-center">
          <div className="bg-black text-white p-8 rounded-lg lg:w-[45vw] z-50 w-[70vw] lg:max-h-[75vh] overflow-y-auto">
            <h2 className="text-2xl mb-4 montserrat">Pyet per cmimin</h2>

            <form onSubmit={sendEmail}>
              <div className="mb-4">
                <label className="block text-sm font-medium montserrat mb-2">Name</label>
                <input type="text" name="name" value={formData.name} onChange={handleInputChange} className="w-full p-2 rounded bg-[#0F0F0F] text-white montserrat" required />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium montserrat mb-2">Phone</label>
                <input type="tel" name="phone" value={formData.phone} onChange={handleInputChange} className="w-full p-2 rounded bg-[#0F0F0F] text-white montserrat" required />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium montserrat mb-2">Email</label>
                <input type="email" name="email" value={formData.email} onChange={handleInputChange} className="w-full p-2 rounded bg-[#0F0F0F] text-white montserrat" required />
              </div>
             <div className="mb-4">
                <p className="text-sm montserrat font-medium my-3 main-red-bg w-fit px-3 py-2">Kati {customUrl3}</p>
              </div>
              <div className="mb-4">
                <img src={formData.image} alt="Selected Apartment" className="w-full h-auto mb-2" />
              </div>
              <button type="submit" className="main-red-bg px-4 py-2 bg-white text-white rounded montserrat">Dergo</button>
            </form>
            <button onClick={() => setShowForm(false)} className="absolute top-[10vh] lg:top-[3vw] right-[15vw] lg:right-[25vw] text-white main-red-bg px-[5px] py-[2px] montserrat">X</button>
          </div>
        </div>
      )}

      <ToastContainer />
    </div>
  );
};

export default NavigateFloor;
